import {
  CalendarPicker,
  Checkbox,
  UserSelectMultiValueLabel,
  UserSelectOption,
  UserSelectPlaceholder,
  UserSelectSingleValue,
  userSelectStyles,
} from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import { PlateRenderElementProps, TElement, useEditorRef, Value } from '@udecode/plate';
import cn from 'classnames';
import { formatISO, parseISO } from 'date-fns';
import * as React from 'react';
import Select from 'react-select';
import { Node, Transforms } from 'slate';
import { ReactEditor, useReadOnly } from 'slate-react';
import { useDebounce } from 'use-debounce';

import { useSlatePluginsStore } from '../store';
import mTstyles from './micro-task.module.css';

const { useCallback, useMemo, useEffect } = React;

type MicroTaskElementProps = PlateRenderElementProps<Value> &
  TElement & {
    element: {
      id: string;
      assignedDates: any;
      members: {
        label: string;
        value: string;
        memberId: string;
        avatar: string;
        email: string;
        lastname: string;
        firstname: string;
      }[];
    };
  };

export const MicroTaskElement = ({
  attributes,
  children,
  element,
}: MicroTaskElementProps): JSX.Element => {
  const editor = useEditorRef();
  const readOnly = useReadOnly();
  const { members, assignTask } = useSlatePluginsStore();
  const path = ReactEditor.findPath(editor, element);

  const { checked, isAssign } = element;
  const [debouncedValue] = useDebounce(element, 150);

  useEffect(() => {
    assignTask?.({
      taskId: element.id,
      taskName: [element],
      dateRange: element.assignedDates,
      members: element?.members?.map((member) => member.memberId),
      completed: !!element.checked,
    });
  }, [debouncedValue]);

  const handleChange = useCallback(
    (isChecked: boolean) => {
      Transforms.setNodes(editor, { checked: isChecked } as Partial<Node>, { at: path });
    },
    [editor, path]
  );

  const handleOnChangeMembers = useCallback(
    (values) => {
      const people = values?.map((obj) => ({
        memberId: obj.memberId,
        avatar: obj.avatar,
        lastname: obj.lastname,
        email: obj.email,
        firstname: obj.firstname,
      }));

      Transforms.setNodes(editor, { members: people } as Partial<Node>, { at: path });
    },
    [editor, path, element]
  );

  const handleOnChangeDate = useCallback(
    (dates) => {
      const assignedDates = dates.length
        ? dates?.map((date) => formatISO(date))
        : [formatISO(dates)];

      Transforms.setNodes(editor, { assignedDates: assignedDates } as Partial<Node>, { at: path });
    },
    [path, editor]
  );

  const checkbox = useMemo(() => {
    return (
      <Checkbox
        wrapperClass="microtask"
        checked={!!checked}
        isDisabled={!!readOnly}
        color="#2BC545"
        onChange={handleChange}
      />
    );
  }, [checked, readOnly, handleChange]);

  const string = Node.string(element);

  const assignUsers = useMemo(() => {
    return (
      <div className="w-full flex mt-16" contentEditable={false}>
        <div className="w-full mr-20">
          <Select
            options={[
              {
                label: 'Select people',
                options: members.map((obj) => ({
                  label: obj.firstname,
                  value: obj.memberId,
                  memberId: obj.memberId,
                  avatar: obj.avatar,
                  email: obj.email,
                  lastname: obj.lastname,
                  firstname: obj.firstname,
                })),
              },
            ]}
            value={element.members?.map((obj) => ({
              label: obj.firstname,
              value: obj.memberId,
              memberId: obj.memberId,
              avatar: obj.avatar,
              email: obj.email,
              lastname: obj.lastname,
              firstname: obj.firstname,
            }))}
            onChange={handleOnChangeMembers}
            placeholder={<UserSelectPlaceholder label="Assign to" />}
            styles={{
              ...userSelectStyles,
              control: (styles) => ({
                ...styles,
                ...(userSelectStyles.control ? userSelectStyles.control(styles, {} as any) : {}),
                border: 'none',
                background: '#F6F7F9',
              }),
            }}
            isMulti
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
              SingleValue: UserSelectSingleValue,
              Option: UserSelectOption,
              MultiValueLabel: UserSelectMultiValueLabel,
            }}
          />
        </div>
        <div className="w-full">
          <Tippy content={<span>Issue date</span>}>
            <button className="w-full">
              <CalendarPicker
                value={
                  element.assignedDates?.length &&
                  element.assignedDates?.map((date) => parseISO(date))
                }
                onChange={handleOnChangeDate}
                inputClassName="date-picker bg-gray-100 p-12 w-full rounded-sm h-48 text-left"
              />
            </button>
          </Tippy>
        </div>
      </div>
    );
  }, [members, element, handleOnChangeMembers, handleOnChangeDate]);

  return (
    <div {...attributes} className={cn('pt-3 pl-35 mt-32 mb-24 relative')}>
      <span
        className={cn('font-medium text-xl', mTstyles.text)}
        contentEditable={!readOnly}
        placeholder={!string ? 'Task' : undefined}
        suppressContentEditableWarning={true}
      >
        {children}
      </span>
      <div contentEditable={false} className="mt-8 absolute top-0 left-0">
        {checkbox}
      </div>
      {isAssign ? assignUsers : null}
    </div>
  );
};
